import React from 'react';
import { CircularProgress } from '@mui/material';

export interface SyncProgressDetails {
    isRunning: boolean;
    progress: {
        totalPages: number;
        processedPages: number;
        totalFetched: number;
        created: number;
        updated: number;
        failed: number;
    };
    lastError?: string;
}

interface SyncProgressBannerProps {
    syncing: boolean;
    details: SyncProgressDetails | null;
}

export const SyncProgressBanner: React.FC<SyncProgressBannerProps> = ({ syncing, details }) => {
    if (!syncing) return null;

    return (
        <div className="mt-4 mb-6 p-4 rounded-lg bg-blue-50 border border-blue-200 flex flex-col gap-3 shadow-sm">
            <div className="flex items-center gap-3">
                <CircularProgress size={20} thickness={5} />
                <span className="text-blue-800 font-semibold italic">
                    {details
                        ? `Syncing in progress... ${details.progress.processedPages} / ${details.progress.totalPages} pages`
                        : 'Starting sync... Please wait.'
                    }
                </span>
            </div>

            {details && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm bg-white p-3 rounded border border-blue-100">
                    <div className="flex flex-col">
                        <span className="text-gray-500 text-xs uppercase tracking-wider">Fetched</span>
                        <span className="font-bold text-blue-700">{details.progress.totalFetched}</span>
                    </div>
                    <div className="flex flex-col">
                        <span className="text-gray-500 text-xs uppercase tracking-wider">Created</span>
                        <span className="font-bold text-green-600">{details.progress.created}</span>
                    </div>
                    <div className="flex flex-col">
                        <span className="text-gray-500 text-xs uppercase tracking-wider">Updated</span>
                        <span className="font-bold text-orange-600">{details.progress.updated}</span>
                    </div>
                    <div className="flex flex-col">
                        <span className="text-gray-500 text-xs uppercase tracking-wider">Failed</span>
                        <span className="font-bold text-red-600">{details.progress.failed}</span>
                    </div>
                </div>
            )}

            {details?.lastError && (
                <div className="text-xs text-red-600 mt-1 p-2 bg-red-50 rounded border border-red-100">
                    <strong>Status:</strong> {details.lastError}
                </div>
            )}
        </div>
    );
};
